import ValidationErrorModal from '@/components/validation-error-modal';
import PromptEditor from '@/editor/components/prompt-editor/prompt-editor';
import { useGetVariables } from '@/hooks/queries/variables';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { useNavigation } from '@/hooks/useNavigation';
import { useUserStore } from '@/store/use-user-store';
import { TextVariable } from '@/types/variable';
import {
  decodeLegacyHtmlEntities,
  isReservedVariable,
  parsePromptSystemVariableErrors,
  VALID_VARIABLE_PREFIXES,
  validatePromptVariables,
} from '@/utils/promptUtils';
import { Spinner } from '@nextui-org/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAddToConversation,
  useCreateOrUpdatePrompt,
  useGetPosts,
  useGetPromptById,
  useRunPrompt,
} from 'Queries/prompts';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { useParams } from 'react-router-dom';
import type { Agent, Post } from '../../types/agent';
import Header from './components/header';
import ListPosts from './components/list-posts';
import ModalSettings, {
  ModelSettings as ApiModelSettings,
} from './components/modal-settings';
import PreviewResponse from './components/preview-response';
import UnsavedChangesModal from './components/unsaved-changes-modal';

/*
  TODO:
  1. Better error handling, pull up the useFormik for handling form
  2. Standardise the react-query hooks
  3. Toast notifications for showing errors
  4. Add a try/catch blocks
*/

enum TextSource {
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
}

const getLatestUserMessageKey = (promptId: string | number) =>
  `latestUserMessage_${promptId}`;

// Extend the base Agent interface with our additional properties
interface ExtendedAgent extends Omit<Agent, 'id'> {
  id: string | null;
  text_variables: Record<string, string>;
  enable_audio: boolean;
  tags?: any[];
}

// Define the PromptData interface to match the API response
interface ApiPromptData {
  id: string;
  name: string;
  system_prompt: string;
  is_active: boolean;
  text_variables?: TextVariable[];
  model_settings?: ApiModelSettings[];
  tags?: string[];
  enable_audio?: boolean;
}

const toModelSettingsPayload = (settings: ApiModelSettings) => ({
  name: settings.name,
  temperature: settings.temperature,
  max_tokens: settings.max_tokens,
  ...(settings.top_p !== undefined && { top_p: settings.top_p }),
  ...(settings.frequency_penalty !== undefined && {
    frequency_penalty: settings.frequency_penalty,
  }),
  ...(settings.presence_penalty !== undefined && {
    presence_penalty: settings.presence_penalty,
  }),
});

type FormSnapshot = {
  name: string;
  prompt: string;
};

const createEmptyPosts = (): Post[] => [
  { text_src: TextSource.USER, text: '', mandatory: true, pairId: null },
];

const createSnapshot = (name: string, prompt: string): FormSnapshot => ({
  name: name.trim(),
  prompt: prompt.trim(),
});

const hasUnsavedChanges = (
  agent: ExtendedAgent,
  saved: FormSnapshot | null
): boolean => {
  if (!saved) return false;

  const current = createSnapshot(agent.name, agent.prompt ?? '');

  return current.name !== saved.name || current.prompt !== saved.prompt;
};

const isUserPost = (post: Post) =>
  post.text_src === TextSource.USER || post.text_src === 'User';

const getLatestEditableUserPost = (posts: Post[]) =>
  [...posts].reverse().find((post) => !post.id && isUserPost(post));

const buildPostsFromData = (
  postsResults: Post[] | undefined,
  promptSlug: string
): Post[] => {
  if (!postsResults?.length) {
    return [
      {
        text_src: TextSource.USER,
        text: localStorage.getItem(getLatestUserMessageKey(promptSlug)) || '',
      },
    ];
  }

  const newPosts = [...postsResults];
  const lastPost = postsResults[postsResults.length - 1];

  if (lastPost.text_src === TextSource.ASSISTANT) {
    newPosts.push({
      text_src: TextSource.USER,
      text: localStorage.getItem(getLatestUserMessageKey(promptSlug)) || '',
    });
  }

  return newPosts;
};

export default function AgentPage() {
  const { id: promptSlug } = useParams();
  const [agent, setAgent] = useState<ExtendedAgent>({
    id: null,
    name: '',
    description: '',
    isActive: true,
    prompt: '',
    version: 'v1',
    examples: [],
    text_variables: {},
    enable_audio: false,
  });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [modelSettings, setModelSettings] = useState<ApiModelSettings>({
    name: 'gpt-4o',
    temperature: 1,
    max_tokens: 1024,
    top_p: 1,
    frequency_penalty: 0,
    presence_penalty: 0,
  });
  const [generatedResponse, setGeneratedResponse] = useState<string>('');
  const [posts, setPosts] = useState<Post[]>([
    { text_src: TextSource.USER, text: '', mandatory: true, pairId: null },
  ]);
  const [showRunValidation, setShowRunValidation] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationModalDescription, setValidationModalDescription] = useState(
    'The following variables have invalid formats:'
  );
  const [showValidationFormatGuide, setShowValidationFormatGuide] =
    useState(true);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [saveFieldErrors, setSaveFieldErrors] = useState<{
    name?: string;
    prompt?: string;
  }>({});
  const [savedSnapshot, setSavedSnapshot] = useState<FormSnapshot | null>(null);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { navigateToTenantRoute } = useNavigation();
  const [leftPanelWidth, setLeftPanelWidth] = useState(window.innerWidth * 0.5);
  const userId = useUserStore((state) => state.user?.id);
  const { tenantId } = useActiveTenant();
  // const [variablesState, setVariablesState] = useState<{
  //   open: boolean;
  //   currentValues: Record<string, string>;
  // }>({
  //   open: false,
  //   currentValues: {},
  // });

  const {
    data: promptData,
    isLoading: isPromptLoading,
    isError: isPromptError,
    error: promptError,
    refetch: refetchPromptById,
  } = useGetPromptById(promptSlug, false);

  const { mutateAsync: createOrUpdatePrompt, isPending: isPromptSaving } =
    useCreateOrUpdatePrompt({
      promptSlug,
      onSuccess: () => {
        refetchPromptById();
        setIsSettingsOpen(false);
      },
      onError: (error) => {
        const systemVariableErrors = parsePromptSystemVariableErrors(error);
        if (systemVariableErrors.length > 0) {
          setValidationErrors([systemVariableErrors.join(', ')]);
          setValidationModalDescription(
            'The following variables must be declared before they can be used in the prompt:'
          );
          setShowValidationFormatGuide(false);
          setShowValidationModal(true);
          return;
        }

        toast.error('Failed to update prompt. Please try again.');
        console.error('Failed to update prompt:', error);
      },
    });

  const { mutate: runPromptMutation, isPending: isRunLoading } = useRunPrompt({
    onSuccess: (data) => {
      setShowRunValidation(false);
      setGeneratedResponse(data);
      localStorage.removeItem(getLatestUserMessageKey(promptSlug!));
    },
  });

  const { data: postsData, refetch: refetchPosts } = useGetPosts(promptSlug);

  const { mutate: addToConversationMutation, isPending: addingToConversation } =
    useAddToConversation({
      promptId: agent.id ?? promptSlug ?? '',
      onSuccess: (data) => {
        const resp = data?.results || data;
        if (resp) {
          setGeneratedResponse('');
          refetchPosts();
        }
      },
    });

  // Add variables query
  const { data: variablesData } = useGetVariables({}, true);

  // Update agent's text_variables when variables data changes
  useEffect(() => {
    if (!variablesData?.results?.length || !agent.id) return;

    const agentId = agent.id.toString();

    // First, get shared variables (parent_object_id is null)
    const sharedVariables = variablesData.results.filter(
      (variable) => variable.parent_object_id === null
    );

    const tenantVariables = variablesData.results.filter(
      (variable) =>
        variable.parent_object_id?.toString() === tenantId?.toString()
    );

    // Then get agent-specific variables
    const agentVariables = variablesData.results.filter(
      (variable) =>
        variable.parent_object_id?.toString() === agentId ||
        variable.parent_object_id?.toString() === userId
    );

    if (!sharedVariables.length && !agentVariables.length) return;

    // Create a map of all available variables
    const availableVariablesMap = new Map();

    // First add shared variables
    sharedVariables.forEach((variable) => {
      variable.text_variables.forEach((textVar) => {
        availableVariablesMap.set(textVar.title, textVar.value);
      });
    });

    tenantVariables.forEach((variable) => {
      variable.text_variables.forEach((textVar) => {
        availableVariablesMap.set(textVar.title, textVar.value);
      });
    });

    // Then add/override with agent-specific variables
    agentVariables.forEach((variable) => {
      variable.text_variables.forEach((textVar) => {
        if (!agent.text_variables?.[textVar.title]) {
          // Only set if the variable doesn't already exist in agent
          availableVariablesMap.set(textVar.title, textVar.value);
        }
      });
    });

    // Only update if we have new variables to add
    if (availableVariablesMap.size > 0) {
      const existingVariables = agent.text_variables || {};
      let hasChanges = false;
      const updatedVariables = {} as Record<string, string>;

      // Check for both new variables and changes to existing ones
      availableVariablesMap.forEach((value, title) => {
        if (!(title in existingVariables)) {
          // New variable
          hasChanges = true;
          updatedVariables[title] = value;
        } else if (existingVariables[title] !== value) {
          // Existing variable with changed value
          hasChanges = true;
          updatedVariables[title] = value;
        }
      });

      // Only trigger state update if we have new or changed variables
      if (hasChanges) {
        setAgent((prev) => ({
          ...prev,
          text_variables: {
            ...prev.text_variables,
            ...updatedVariables,
          },
        }));
      }
    }
  }, [variablesData?.results, agent.text_variables]);

  useEffect(() => {
    if (!promptSlug) {
      setSavedSnapshot(createSnapshot('', ''));
      return;
    }

    if (!promptData) return;

    const currentPrompt = promptData as unknown as ApiPromptData;

    // Same decoded text the editor shows, so decoding alone doesn't count as
    // an unsaved change
    setSavedSnapshot(
      createSnapshot(
        currentPrompt.name,
        decodeLegacyHtmlEntities(currentPrompt.system_prompt ?? '')
      )
    );
  }, [promptSlug, promptData]);

  useEffect(() => {
    if (!promptSlug || !promptData) return;

    const currentPrompt = promptData as unknown as ApiPromptData;
    const textVariablesMap =
      currentPrompt.text_variables?.reduce(
        (acc: Record<string, string>, item: TextVariable) => ({
          ...acc,
          [item.title]: item.value,
        }),
        {}
      ) || {};
    setAgent({
      id: currentPrompt.id || null,
      name: currentPrompt.name,
      description: '',
      isActive: currentPrompt.is_active,
      // Shown without the old editor's HTML codes; saved that way on Save
      prompt: decodeLegacyHtmlEntities(currentPrompt.system_prompt ?? ''),
      version: 'v1',
      examples: [],
      text_variables: textVariablesMap,
      tags: currentPrompt.tags,
      enable_audio: currentPrompt.enable_audio || false,
    });

    if (
      currentPrompt.model_settings &&
      currentPrompt.model_settings.length > 0
    ) {
      setModelSettings(toModelSettingsPayload(currentPrompt.model_settings[0]));
    }
  }, [promptData, promptSlug]);

  useEffect(() => {
    if (!promptSlug || !postsData?.results) return;

    setPosts(buildPostsFromData(postsData.results, promptSlug));
  }, [postsData, promptSlug]);

  const handleSave = async (
    modalSettings?: ApiModelSettings
  ): Promise<void> => {
    setShowRunValidation(false);
    // Also decoded here in case codes were typed or pasted since loading
    const promptContent = decodeLegacyHtmlEntities(agent.prompt ?? '');
    const fieldErrors: { name?: string; prompt?: string } = {};

    if (!agent.name.trim()) {
      fieldErrors.name = 'Name is required';
    }
    if (!promptContent.trim()) {
      fieldErrors.prompt = 'Prompt is required';
    }

    if (fieldErrors.name || fieldErrors.prompt) {
      setSaveFieldErrors(fieldErrors);
      toast.error('Please fill in all required fields');
      return;
    }

    setSaveFieldErrors({});

    // Validate variables before saving
    const variableValidation = validatePromptVariables(promptContent);
    if (!variableValidation.isValid) {
      setValidationErrors(variableValidation.errors);
      setValidationModalDescription(
        variableValidation.errors.some((error) => error.includes('placeholder'))
          ? 'Fix the following variable placeholder syntax issues:'
          : 'The following variables have invalid formats:'
      );
      setShowValidationFormatGuide(true);
      setShowValidationModal(true);
      return;
    }

    // Clear validation errors if validation passes
    setValidationErrors([]);

    const textVariables: Record<string, string>[] = [];
    // Filter out variables that start with 'user.'
    const variableKeys = Object.keys(agent?.text_variables || {}).filter(
      (key) => !isReservedVariable(key)
    );

    variableKeys.forEach((key) => {
      const value = agent?.text_variables?.[key];
      if (value !== undefined) {
        textVariables.push({
          title: key,
          value: value,
        });
      }
    });

    const payload = {
      name: agent.name.trim(),
      system_prompt: promptContent,
      is_active: agent.isActive || true,
      text_variables: textVariables,
      enable_audio: agent.enable_audio || false,
      ...(modalSettings && {
        model_settings: [toModelSettingsPayload(modalSettings)],
      }),
    };

    await createOrUpdatePrompt({
      id: promptSlug ? agent.id : null,
      data: payload,
    });

    setSavedSnapshot(createSnapshot(agent.name, promptContent));
  };

  const addMessagePair = (): void => {
    const pairId = Date.now();
    setPosts([
      ...posts,
      ...[
        { text_src: TextSource.ASSISTANT, text: '', pairId },
        { text_src: TextSource.USER, text: '', pairId },
      ],
    ]);
  };

  const handleRun = async () => {
    setShowRunValidation(true);

    const latestUserPost = getLatestEditableUserPost(posts);
    if (!latestUserPost?.text.trim()) {
      return;
    }

    if (latestUserPost && promptSlug) {
      localStorage.setItem(
        getLatestUserMessageKey(promptSlug),
        latestUserPost.text
      );
    }

    if (promptData?.id) {
      runPromptMutation({
        promptId: promptData.id,
        text: latestUserPost.text,
      });
    }
  };

  const resetStates = () => {
    queryClient.removeQueries({ queryKey: ['prompt'] });
    queryClient.removeQueries({ queryKey: ['posts'] });
    const emptyPosts = createEmptyPosts();
    setAgent({
      id: null,
      name: '',
      description: '',
      isActive: true,
      prompt: '',
      version: 'v1',
      examples: [],
      text_variables: {},
      enable_audio: false,
    });
    setPosts(emptyPosts);
    setGeneratedResponse('');
    setShowRunValidation(false);
    setSaveFieldErrors({});
    setValidationErrors([]);
    setShowValidationModal(false);
    setSavedSnapshot(createSnapshot('', ''));
  };

  const proceedToCreatePrompt = () => {
    setIsLeaveModalOpen(false);
    queryClient.removeQueries({ queryKey: ['prompt'] });
    queryClient.removeQueries({ queryKey: ['posts'] });
    navigateToTenantRoute('/agents/create', { replace: true });
    resetStates();
  };

  const handleCreatePrompt = () => {
    if (hasUnsavedChanges(agent, savedSnapshot)) {
      setIsLeaveModalOpen(true);
      return;
    }

    proceedToCreatePrompt();
  };

  const onResize = (_event: any, { size }: { size: { width: number } }) => {
    setLeftPanelWidth(size.width);
  };

  if (isPromptLoading) {
    return (
      <div className="min-h-screen bg-background p-5 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isPromptError && promptSlug) {
    const isForbidden =
      isAxiosError(promptError) && promptError.response?.status === 403;
    const message = isForbidden
      ? 'You do not have permission to edit this agent. If you recently signed in with another account, sign out and sign back in with an admin account.'
      : 'Unable to load this agent. Please try again or contact support.';

    return (
      <div className="min-h-screen bg-background p-8 flex items-center justify-center">
        <div className="max-w-lg text-center space-y-4">
          <h1 className="text-2xl font-bold">Cannot open agent editor</h1>
          <p className="text-default-600">{message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-2 sm:p-8">
      <h1 className="min-w-0 flex-1 truncate text-2xl font-bold">
        {promptSlug ? 'Edit Prompt' : 'Create Prompt'}
      </h1>

      <Header
        agent={agent}
        setAgent={setAgent}
        nameError={saveFieldErrors.name}
        onNameChange={(name: string) => {
          setAgent((prev) => ({ ...prev, name }));
          if (saveFieldErrors.name) {
            setSaveFieldErrors((prev) => ({ ...prev, name: undefined }));
          }
        }}
        isCreateLoading={isPromptSaving}
        handleSave={() => void handleSave()}
        isRunLoading={isRunLoading}
        handleRun={handleRun}
        setIsSettingsOpen={setIsSettingsOpen}
        posts={posts}
        onCreatePrompt={handleCreatePrompt}
        refetchPrompt={refetchPromptById}
      />
      {/* Main Content */}
      <div className="flex h-[calc(100vh-8rem)] relative">
        {/* Left Panel */}
        <Resizable
          width={leftPanelWidth}
          height={0}
          onResize={onResize}
          handle={
            <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary-300 z-10" />
          }
          resizeHandles={['e']}
          // maxConstraints={[1000, 200]}
          // minConstraints={[500, 200]}
        >
          <div
            style={{ width: leftPanelWidth }}
            className="py-4 pr-4 border-r border-divider overflow-y-auto !min-h-[200px]"
          >
            {/* <PromptEditor
              initialName="My Custom Prompt"
              initialVersion="1.1.0"
              onSave={handleSave}
              // onImport={null}
            /> */}
            <PromptEditor
              key={promptSlug ?? 'create'}
              initialContent={agent.prompt}
              onContentChange={(value, variables) => {
                const processedVariables = Object.fromEntries(
                  Object.entries(variables).map(([key, value]) => [
                    key,
                    value.replace(/<[^>]*>/g, '').trim(),
                  ])
                );
                setAgent((prev) => ({
                  ...prev,
                  prompt: value,
                  text_variables: processedVariables,
                }));
                if (saveFieldErrors.prompt) {
                  setSaveFieldErrors((prev) => ({
                    ...prev,
                    prompt: undefined,
                  }));
                }
              }}
              variables={agent?.text_variables || {}}
              isInvalid={Boolean(saveFieldErrors.prompt)}
              errorMessage={saveFieldErrors.prompt}
            />
            <ListPosts
              posts={posts}
              setPosts={setPosts}
              showRunValidation={showRunValidation}
              addMessagePair={addMessagePair}
              promptId={Number(agent.id)}
            />
          </div>
        </Resizable>

        {/* Right Panel - Preview */}
        <div
          style={{ width: `calc(100% - ${leftPanelWidth}px)` }}
          className="bg-default-50 p-4"
        >
          <PreviewResponse
            isRunLoading={isRunLoading}
            generatedResponse={generatedResponse}
            addingToConversation={addingToConversation}
            promptData={promptData}
            posts={posts}
            setPosts={setPosts}
            addToConversationMutation={addToConversationMutation}
          />
        </div>
      </div>
      <ModalSettings
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={modelSettings}
        onSettingsChange={setModelSettings}
        handleSave={(settings) => void handleSave(settings)}
        isLoading={isPromptSaving}
      />

      {/* Validation Error Modal */}
      {showValidationModal && (
        <ValidationErrorModal
          isOpen={showValidationModal}
          onClose={() => setShowValidationModal(false)}
          errors={validationErrors}
          validPrefixes={[...VALID_VARIABLE_PREFIXES]}
          description={validationModalDescription}
          showFormatGuide={showValidationFormatGuide}
        />
      )}

      <UnsavedChangesModal
        isOpen={isLeaveModalOpen}
        onCancel={() => setIsLeaveModalOpen(false)}
        onLeave={proceedToCreatePrompt}
      />

      {/* <VariableDrawer
        isOpen={variablesState.open}
        variables={variablesState.currentValues}
        onClose={() =>
          setVariablesState({
            open: false,
            currentValues: variablesState.currentValues,
          })
        }
        // onSubmit={(values) => {
        //   console.log(values);
        //   setVariablesState({
        //     open: false,
        //     currentValues: values,
        //   });
        // }}
        setVariablesState={(variable) => {
          setVariablesState((prev) => ({
            ...prev,
            currentValues: { ...prev.currentValues, ...variable },
          }));
        }}
      /> */}
    </div>
  );
}
